import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { whiteboardApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Save, Pencil, Eraser, Square, Circle, Minus, Type, Undo2, Redo2, Trash2, Download, Plus, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const TOOLS = [
  { id: 'pen', icon: Pencil, label: 'Pen' },
  { id: 'eraser', icon: Eraser, label: 'Eraser' },
  { id: 'line', icon: Minus, label: 'Line' },
  { id: 'rect', icon: Square, label: 'Rectangle' },
  { id: 'circle', icon: Circle, label: 'Circle' },
  { id: 'text', icon: Type, label: 'Text' },
];

const COLORS = ['#000000', '#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#ffffff'];
const SIZES = [2, 4, 6, 10, 16];

export default function WhiteboardPage() {
  const { wbId } = useParams();
  const navigate = useNavigate();
  const isNew = !wbId;
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(4);
  const [title, setTitle] = useState('');
  const [wbData, setWbData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [showList, setShowList] = useState(isNew);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');

  useEffect(() => {
    if (wbId) {
      whiteboardApi.get(wbId).then(wb => {
        setWbData(wb);
        setTitle(wb.title);
        setShowList(false);
        setLoading(false);
        if (wb.canvas_data) {
          setTimeout(() => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            const img = new window.Image();
            img.onload = () => { ctx.drawImage(img, 0, 0); saveState(); };
            img.src = wb.canvas_data;
          }, 100);
        } else { setTimeout(() => { clearCanvas(); saveState(); }, 100); }
      }).catch(() => { toast.error('Not found'); navigate('/whiteboard'); });
    } else {
      whiteboardApi.list().then(d => { setBoards([...d.owned, ...d.shared]); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [wbId]);

  const saveState = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL();
    setHistory(prev => [...prev.slice(0, historyIdx + 1), data]);
    setHistoryIdx(prev => prev + 1);
  };

  const undo = () => {
    if (historyIdx <= 0) return;
    const newIdx = historyIdx - 1;
    setHistoryIdx(newIdx);
    restoreState(history[newIdx]);
  };

  const redo = () => {
    if (historyIdx >= history.length - 1) return;
    const newIdx = historyIdx + 1;
    setHistoryIdx(newIdx);
    restoreState(history[newIdx]);
  };

  const restoreState = (dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.onload = () => { ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0); };
    img.src = dataUrl;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const handleMouseDown = (e) => {
    const pos = getPos(e);
    setIsDrawing(true);
    setStartPos(pos);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (tool === 'pen' || tool === 'eraser') {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
      ctx.lineWidth = tool === 'eraser' ? lineWidth * 3 : lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    if (tool === 'text') {
      const text = prompt('Enter text:');
      if (text) {
        ctx.font = `${lineWidth * 4}px sans-serif`;
        ctx.fillStyle = color;
        ctx.fillText(text, pos.x, pos.y);
        saveState();
      }
      setIsDrawing(false);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const pos = getPos(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (tool === 'pen' || tool === 'eraser') {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
  };

  const handleMouseUp = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const pos = getPos(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (tool === 'line' && startPos) {
      ctx.beginPath();
      ctx.moveTo(startPos.x, startPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
    if (tool === 'rect' && startPos) {
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.strokeRect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y);
    }
    if (tool === 'circle' && startPos) {
      const rx = Math.abs(pos.x - startPos.x) / 2;
      const ry = Math.abs(pos.y - startPos.y) / 2;
      const cx = startPos.x + (pos.x - startPos.x) / 2;
      const cy = startPos.y + (pos.y - startPos.y) / 2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    }
    saveState();
  };

  const handleSave = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      const canvasData = canvas.toDataURL();
      if (wbId) {
        await whiteboardApi.update(wbId, { canvas_data: canvasData, title });
      } else {
        const wb = await whiteboardApi.create({ title: title || 'Untitled Whiteboard', canvas_data: canvasData });
        navigate(`/whiteboard/${wb.whiteboard_id}`, { replace: true });
      }
      toast.success('Saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${title || 'whiteboard'}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const handleCreate = async () => {
    try {
      const wb = await whiteboardApi.create({ title: 'Untitled Whiteboard' });
      navigate(`/whiteboard/${wb.whiteboard_id}`);
    } catch { toast.error('Failed to create'); }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this whiteboard?')) return;
    try {
      await whiteboardApi.delete(id);
      setBoards(prev => prev.filter(b => b.whiteboard_id !== id));
      toast.success('Deleted');
    } catch (err) { toast.error(err.message); }
  };

  const handleShare = async () => {
    if (!shareEmail.trim() || !wbId) return;
    try {
      await whiteboardApi.share(wbId, shareEmail);
      toast.success('Shared!');
      setShowShare(false); setShareEmail('');
    } catch (err) { toast.error(err.message); }
  };

  // List view
  if (showList) {
    return (
      <div className="min-h-screen bg-background" data-testid="whiteboard-list">
        <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/suite')}><ArrowLeft className="w-5 h-5" /></Button>
              <Pencil className="w-6 h-6 text-purple-500" />
              <h1 className="text-xl font-bold text-foreground">Whiteboard</h1>
            </div>
            <Button onClick={handleCreate} data-testid="new-wb-btn"><Plus className="w-4 h-4 mr-1" /> New Whiteboard</Button>
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-4 py-8">
          {loading ? (
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">{[1,2,3].map(i => <div key={i} className="h-36 bg-muted animate-pulse rounded-xl" />)}</div>
          ) : boards.length === 0 ? (
            <div className="text-center py-20">
              <Pencil className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">No whiteboards yet</h2>
              <p className="text-muted-foreground mb-6">Create your first whiteboard to start drawing.</p>
              <Button onClick={handleCreate}>Create Whiteboard</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
              {boards.map(b => (
                <div key={b.whiteboard_id} onClick={() => navigate(`/whiteboard/${b.whiteboard_id}`)} className="group cursor-pointer" data-testid={`wb-${b.whiteboard_id}`}>
                  <div className="rounded-xl border border-border bg-card hover:shadow-md transition-shadow overflow-hidden">
                    <div className="h-28 bg-white flex items-center justify-center">
                      {b.thumbnail ? <img src={b.thumbnail} alt="" className="w-full h-full object-cover" /> : <Pencil className="w-10 h-10 text-muted-foreground/20" />}
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground truncate">{b.title}</h3>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-muted-foreground">{new Date(b.updated_at).toLocaleDateString()}</span>
                        <button onClick={e => handleDelete(b.whiteboard_id, e)} className="p-1 hover:bg-muted rounded opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  // Drawing view
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="whiteboard-editor">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="px-4 py-2 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/whiteboard')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <Input value={title} onChange={e => setTitle(e.target.value)} className="text-lg font-semibold border-none shadow-none focus-visible:ring-0 px-1 h-auto w-64" data-testid="wb-title" />
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowShare(true)}><Share2 className="w-4 h-4 mr-1" /> Share</Button>
            <Button variant="ghost" size="sm" onClick={handleDownload}><Download className="w-4 h-4 mr-1" /> Export</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-btn"><Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </div>
        {/* Toolbar */}
        <div className="px-4 py-1.5 flex items-center gap-2 border-t border-border flex-wrap">
          {TOOLS.map(t => (
            <button key={t.id} onClick={() => setTool(t.id)} title={t.label}
              className={`p-2 rounded transition-colors ${tool === t.id ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:bg-muted'}`}
              data-testid={`tool-${t.id}`}>
              <t.icon className="w-4 h-4" />
            </button>
          ))}
          <div className="w-px h-6 bg-border mx-1" />
          <button onClick={undo} className="p-2 rounded hover:bg-muted text-foreground/70" title="Undo"><Undo2 className="w-4 h-4" /></button>
          <button onClick={redo} className="p-2 rounded hover:bg-muted text-foreground/70" title="Redo"><Redo2 className="w-4 h-4" /></button>
          <button onClick={() => { clearCanvas(); saveState(); }} className="p-2 rounded hover:bg-muted text-foreground/70" title="Clear"><Trash2 className="w-4 h-4" /></button>
          <div className="w-px h-6 bg-border mx-1" />
          {COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)}
              className={`w-6 h-6 rounded-full border-2 transition-all ${color === c ? 'border-primary scale-110' : 'border-border'}`}
              style={{ backgroundColor: c }} />
          ))}
          <div className="w-px h-6 bg-border mx-1" />
          {SIZES.map(s => (
            <button key={s} onClick={() => setLineWidth(s)}
              className={`w-7 h-7 rounded flex items-center justify-center ${lineWidth === s ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}>
              <div className="rounded-full bg-current" style={{ width: s + 2, height: s + 2 }} />
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center bg-muted/30 p-4 overflow-auto">
        <canvas ref={canvasRef} width={1200} height={700}
          className="bg-white shadow-lg rounded cursor-crosshair"
          style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 120px)' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => { if (isDrawing) { setIsDrawing(false); saveState(); } }}
          data-testid="whiteboard-canvas" />
      </div>

      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent>
          <DialogHeader><DialogTitle>Share Whiteboard</DialogTitle></DialogHeader>
          <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="Enter user email..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShare(false)}>Cancel</Button>
            <Button onClick={handleShare}>Share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
